import { memo, useCallback } from 'react';
import { Pressable } from 'react-native';
import { Box, Button, Icon, Text } from '@bonanza-social/moonlight';
import { useTranslation } from '@bonanza-social/i18n';
import { selectStatus, useStatusPerks } from '@bonanza-social/status';
import { useDispatch, useSelector } from '@bonanza-social/toolkit/state';
import { openChat } from '@bonanza-social/chat/state';
import { ChatButtonProps } from './ChatButton.types';

function ChatButton({ display, showSupportAgentIcon }: ChatButtonProps) {
  const dispatch = useDispatch();
  const [t] = useTranslation('chat');
  const status = useSelector(selectStatus);
  const { hasPriorityChat } = useStatusPerks();

  const onChatPress = useCallback(() => {
    dispatch(openChat());
  }, [dispatch]);

  if (hasPriorityChat && status?.playerStatus) {
    return (
      <Box position="relative" display={display}>
        <Box
          position="absolute"
          top="-space-600"
          right="space-0"
          bottom="space-0"
          paddingHorizontal="space-300"
          borderRadius="radius-200"
          variant="gradient"
          gradient={`status${status.playerStatus}Background`}
          alignItems="center"
          paddingTop="space-100"
        >
          <Text variant="caption-semi" color="accentSurfacePrimary">
            {t('liveChatPerk', { status: status.playerStatus })}
          </Text>
        </Box>
        <Pressable
          onPress={onChatPress}
          accessibilityRole="button"
          testID="chat-button"
        >
          <Box
            borderRadius="radius-200"
            height={40}
            variant="gradient"
            gradient={`status${status.playerStatus}Background`}
            padding="space-025"
          >
            <Box
              borderRadius="radius-200"
              flexDirection="row"
              gap="space-100"
              centerContent
              paddingHorizontal="space-300"
              backgroundColor="primaryBackground"
              flex={1}
            >
              <Icon
                name="supportAgent"
                gradient={`status${status.playerStatus}Background`}
                width={20}
                height={20}
              />
              <Text
                variant="button-small"
                locNS="chat"
                locKey="priorityLiveChatCta"
                textAlign="center"
                numberOfLines={1}
                gradient={`darkSurfaceStatus${status.playerStatus}`}
              />
            </Box>
          </Box>
        </Pressable>
      </Box>
    );
  }

  return (
    <Button
      display={display}
      variant="secondary"
      locNS="chat"
      locKey="liveChatCta"
      onPress={onChatPress}
      leftIcon={showSupportAgentIcon ? 'supportAgent' : undefined}
    />
  );
}

export default memo(ChatButton);
